// Remove the duplicate formatDate function and use the imported one
import React, { useState, useEffect } from "react";
import Header from "./Header";
import CountdownTimer from "./CountdownTimer";
import PrayerSchedule from "./PrayerSchedule";
import LocationSelector from "./LocationSelector";
import NotificationSettings from "./NotificationSettings";
import { PrayerSource } from "./PrayerSourceSelector";
import { usePrayerSource } from "../contexts/PrayerSourceContext";
import { getPrayerTimes, getNextPrayer, fetchPrayerTimes, quietLog, getPrayerTimesBySource } from "../lib/prayerTimesAPI";
import { getCurrentLocation } from "../lib/locationService";
import {
  Clock,
  Sun,
  Sunrise,
  Sunset,
  Moon,
  MountainSnow,
  Sparkles,
  Cog,
  Book,
  Bell
} from "lucide-react";
import { Link } from "react-router-dom";
import { formatDate, getNextDay } from "../lib/dateUtils";
import IftarRestaurants from "./IftarRestaurants";
import { useTranslation } from "react-i18next";
import { Card } from "flowbite-react";

const Home = () => {
  const [location, setLocation] = useState("Stockholm, Sverige");
  const [autoDetectEnabled, setAutoDetectEnabled] = useState(true);
  const [nextPrayer, setNextPrayer] = useState("fajr");
  const { prayerSource } = usePrayerSource();

  const [notificationSettings, setNotificationSettings] = useState({
    enabled: false,
    minutesBefore: 15,
    soundEnabled: true,
  });

  const [prayerData, setPrayerData] = useState(null);
  const [iftarTime, setIftarTime] = useState("17:58");
  const [prayerTimes, setPrayerTimes] = useState([
    {
      name: "Fajr",
      time: "Laddar...",
      icon: <Sunrise className="h-5 w-5 text-amber-500" />,
      isNext: false,
    },
    // Shoruk-bönen har tagits bort härifrån
    {
      name: "Dhuhr",
      time: "Laddar...",
      icon: <Sun className="h-5 w-5 text-yellow-500" />,
      isNext: false,
    },
    {
      name: "Asr",
      time: "Laddar...",
      icon: <Sparkles className="h-5 w-5 text-orange-400" />,
      isNext: false,
    },
    {
      name: "Maghrib",
      time: "Laddar...",
      icon: <Sunset className="h-5 w-5 text-red-500" />,
      isNext: false,
    },
    {
      name: "Isha",
      time: "Laddar...",
      icon: <Moon className="h-5 w-5 text-indigo-400" />,
      isNext: false,
    },
  ]);

  const [nextDayPrayerData, setNextDayPrayerData] = useState(null);
  const [nextDayIftarTime, setNextDayIftarTime] = useState("");

  const { t } = useTranslation();

  useEffect(() => {
    const fetchPrayerTimes = async () => {
      try {
        // Show loading state
        setPrayerTimes((prevTimes) =>
          prevTimes.map((prayer) => ({ ...prayer, time: "Laddar..." })),
        );

        // Fetch today's prayer times
        const data = await getPrayerTimesBySource(prayerSource, location);
        setPrayerData(data);
        setIftarTime(data.prayerTimes.maghrib);

        // Fetch tomorrow's prayer times
        const tomorrow = getNextDay();
        const tomorrowStr = formatDate(tomorrow);
        const nextDayData = await getPrayerTimesBySource(prayerSource, location, tomorrowStr);
        setNextDayPrayerData(nextDayData);
        setNextDayIftarTime(nextDayData.prayerTimes.maghrib);

        // Get next prayer
        const nextPrayerInfo = getNextPrayer(data);
        setNextPrayer(nextPrayerInfo.name.toLowerCase());

        // Create prayer times array for the schedule component
        const prayers = [
          {
            name: "Fajr",
            time: data.prayerTimes.fajr,
            icon: <Sunrise className="h-5 w-5 text-amber-500" />,
            isNext:
              nextPrayerInfo.name === "Fajr" ||
              nextPrayerInfo.name === "Fajr (imorgon)",
          },
          // Shoruk-bönen har tagits bort härifrån
          {
            name: "Dhuhr",
            time: data.prayerTimes.dhuhr,
            icon: <Sun className="h-5 w-5 text-yellow-500" />,
            isNext: nextPrayerInfo.name === "Dhuhr",
          },
          {
            name: "Asr",
            time: data.prayerTimes.asr,
            icon: <Sparkles className="h-5 w-5 text-orange-400" />,
            isNext: nextPrayerInfo.name === "Asr",
          },
          {
            name: "Maghrib",
            time: data.prayerTimes.maghrib,
            icon: <Sunset className="h-5 w-5 text-red-500" />,
            isNext: nextPrayerInfo.name === "Maghrib",
          },
          {
            name: "Isha",
            time: data.prayerTimes.isha,
            icon: <Moon className="h-5 w-5 text-indigo-400" />,
            isNext: nextPrayerInfo.name === "Isha",
          },
        ];

        setPrayerTimes(prayers);
      } catch (error) {
        quietLog("Failed to fetch prayer times", error);
        // Show error state
        setPrayerTimes((prevTimes) =>
          prevTimes.map((prayer) => ({ ...prayer, time: "Fel vid hämtning" })),
        );
      }
    };

    fetchPrayerTimes();

    // Set up a timer to refresh prayer times every hour
    const refreshTimer = setInterval(
      () => {
        fetchPrayerTimes();
      },
      60 * 60 * 1000,
    ); // Every hour

    return () => clearInterval(refreshTimer);
  }, [location, prayerSource]);



  // Lägg till automatisk platsdetektering
  useEffect(() => {
    // Automatisk platsdetektering vid start om det är aktiverat
    if (autoDetectEnabled) {
      const detectLocation = async () => {
        try {
          const locationData = await getCurrentLocation();
          if (locationData) {
            setLocation(locationData.fullLocation);
          }
        } catch (error) {
          quietLog("Kunde inte hämta din position automatiskt", error);
        }
      };

      detectLocation();
    }
  }, [autoDetectEnabled]);

  const handleLocationChange = (newLocation: string) => {
    setLocation(newLocation);
  };

  const handleAutoDetectChange = (enabled: boolean) => {
    setAutoDetectEnabled(enabled);
  };

  const handleNotificationSettingsSave = (enabled: boolean, minutesBefore: number, soundEnabled: boolean) => {
    setNotificationSettings({
      enabled,
      minutesBefore,
      soundEnabled
    });
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50">
      <Header
        location={location}
        onLocationClick={() =>
          document.getElementById("location-dialog-trigger")?.click()
        }
      />

      <main className="container mx-auto pt-4 pb-16 px-4">
        <div className="flex flex-col items-center gap-4">
          {/* Main countdown section - Make this sticky only on mobile */}
          <section className="w-full max-w-4xl mx-auto md:static sticky top-0 z-10 bg-gradient-to-br from-slate-50 to-green-50 pt-2 pb-4">
            <CountdownTimer
              prayerTimesData={prayerData}
            />
          </section>

          {/* Rest of the sections */}
          <section className="w-full max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 md:pt-0 mt-2">
            {/* First column - Prayer schedule */}
            <div className="flex flex-col gap-6 order-1 md:row-span-1">
              <PrayerSchedule
                prayerTimes={prayerTimes}
                iftarTime={iftarTime}
                currentDate={
                  prayerData?.date || new Date().toLocaleDateString("sv-SE")
                }
                location={location}
              />
            </div>

            {/* Location selector - appears third in mobile, below prayer schedule in desktop */}
            <div className="flex flex-col gap-6 order-3 md:order-3 md:col-start-1 md:row-start-2">
              <LocationSelector
                currentLocation={location}
                onLocationChange={handleLocationChange}
                onAutoDetectChange={handleAutoDetectChange}
                autoDetectEnabled={autoDetectEnabled}
              />
            </div>

            {/* Restaurants - appears second in mobile, right column in desktop */}
            <div className="flex flex-col gap-6 order-2 md:order-2 md:row-span-3 md:h-full">
              <IftarRestaurants />
            </div>
          </section>

          {/* Bönehandledning och notifikationer sida vid sida */}
          <section className="w-full max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {/* Bönehandledning */}
            <div className="order-2 md:order-1">
              <Card className="w-full mx-auto overflow-hidden animate-fadeIn shadow-lg">
                <div className="bg-purple-600 text-white p-4 rounded-t-lg">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Book className="h-5 w-5 text-purple-200" />
                    <h2 className="text-xl font-bold">{t('prayerGuide')}</h2>
                  </div>
                </div>

                <div className="p-6 bg-gradient-to-b from-white to-purple-50 dark:from-gray-800 dark:to-purple-950/30">
                  <p className="text-gray-600 dark:text-gray-300 mb-5">
                    {t('prayerGuideDescription')}
                  </p>
                  <Link
                    to="/prayer-instruction"
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-sm font-medium rounded-md transition-colors duration-300"
                  >
                    <Book className="h-4 w-4 mr-2" />
                    {t('viewPrayerGuide')}
                  </Link>
                </div>
              </Card>
            </div>

            {/* Notification settings */}
            <div className="order-1 md:order-2">
              <NotificationSettings
                enabled={notificationSettings.enabled}
                minutesBefore={notificationSettings.minutesBefore}
                soundEnabled={notificationSettings.soundEnabled}
                nextPrayer={nextPrayer}
                onSave={handleNotificationSettingsSave}
              />
            </div>
          </section>
        </div>
      </main >
    </div >
  );
};

export default Home;
